function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function encodeCmmdRuntimeToolActivityPayload(
  kind: string,
  payload: unknown,
): Readonly<Record<string, unknown>> {
  return {
    ...(isRecord(payload) ? payload : { sourcePayload: payload }),
    forgeToolActivity: { schemaVersion: 1, source: "native", kind },
  };
}

export function decodeCmmdRuntimeToolActivityKind(payload: unknown): string | null {
  if (!isRecord(payload)) return null;
  const metadata = payload["forgeToolActivity"];
  if (!isRecord(metadata)) return null;
  const kind = metadata["kind"];
  return metadata["schemaVersion"] === 1 &&
    metadata["source"] === "native" &&
    typeof kind === "string" &&
    kind.startsWith("tool.")
    ? kind
    : null;
}

const WORKTREE_LAUNCH_ACTIVITY_KINDS = new Set([
  "worktree.requested",
  "worktree.stage",
  "worktree.output",
  "worktree.created",
  "worktree.failed",
  "setup-script.requested",
  "setup-script.started",
  "setup-script.skipped",
  "setup-script.failed",
  "launch.agent-starting",
  "launch.cancelled",
]);

export function encodeCmmdRuntimeWorktreeLaunchActivityPayload(
  kind: string,
  payload: unknown,
): Readonly<Record<string, unknown>> {
  return {
    ...(isRecord(payload) ? payload : { sourcePayload: payload }),
    forgeWorktreeLaunchActivity: { schemaVersion: 1, source: "native", kind },
  };
}

export function decodeCmmdRuntimeWorktreeLaunchActivityKind(payload: unknown): string | null {
  if (!isRecord(payload)) return null;
  const metadata = payload["forgeWorktreeLaunchActivity"];
  if (!isRecord(metadata)) return null;
  const kind = metadata["kind"];
  return metadata["schemaVersion"] === 1 &&
    metadata["source"] === "native" &&
    typeof kind === "string" &&
    isCmmdRuntimeWorktreeLaunchActivityKind(kind)
    ? kind
    : null;
}

export function isCmmdRuntimeWorktreeLaunchActivityKind(kind: string): boolean {
  return WORKTREE_LAUNCH_ACTIVITY_KINDS.has(kind);
}

/**
 * A plan update rides the `tool_call` event kind because the forge-context
 * contract has no plan event kind, the same trade the launch rows make. The
 * steps are carried verbatim under a versioned marker so a cloud client can
 * rebuild the composer plan banner from the durable replica. (#3319)
 */
export const CMMD_RUNTIME_PLAN_ACTIVITY_KIND = "turn.plan.updated";

export type CmmdRuntimePlanStep = {
  readonly step: string;
  readonly status: "pending" | "inProgress" | "completed";
};

export function encodeCmmdRuntimePlanActivityPayload(
  kind: string,
  payload: unknown,
): Readonly<Record<string, unknown>> {
  return {
    ...(isRecord(payload) ? payload : { sourcePayload: payload }),
    forgePlanActivity: { schemaVersion: 1, source: "native", kind },
  };
}

export function decodeCmmdRuntimePlanActivity(payload: unknown): {
  readonly kind: typeof CMMD_RUNTIME_PLAN_ACTIVITY_KIND;
  readonly plan: readonly CmmdRuntimePlanStep[];
} | null {
  if (!isRecord(payload)) return null;
  const metadata = payload["forgePlanActivity"];
  if (!isRecord(metadata)) return null;
  if (
    metadata["schemaVersion"] !== 1 ||
    metadata["source"] !== "native" ||
    metadata["kind"] !== CMMD_RUNTIME_PLAN_ACTIVITY_KIND
  ) {
    return null;
  }
  const rawPlan = payload["plan"];
  const plan = Array.isArray(rawPlan)
    ? rawPlan.flatMap((entry): CmmdRuntimePlanStep[] => {
        if (!isRecord(entry)) return [];
        const step = entry["step"];
        const status = entry["status"];
        if (typeof step !== "string" || step.trim().length === 0) return [];
        if (status !== "pending" && status !== "inProgress" && status !== "completed") return [];
        return [{ step, status }];
      })
    : [];
  return { kind: CMMD_RUNTIME_PLAN_ACTIVITY_KIND, plan };
}
